import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";

// #region Styled Components
const SkeletonPane = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(1, 0),
  width: "100%",
}));
/**
 * One message's worth of space, on the side the real message would occupy.
 *
 * The widths mirror `sent-message-container` and `received-message-container`, container queries
 * included, so the placeholder and the message that replaces it are the same size. That is the whole
 * point of drawing this rather than a spinner: nothing moves when the conversation arrives.
 */
const SkeletonMessage = styled("div", {
  shouldForwardProp: (prop) => prop !== "fromCurrentUser",
})<{ fromCurrentUser: boolean }>(({ theme, fromCurrentUser }) => ({
  "@container (min-width: 0px)": {
    maxWidth: "90%",
  },
  "@container (min-width: 600px)": {
    maxWidth: "75%",
  },
  "@container (min-width: 800px)": {
    maxWidth: "50%",
  },
  "@container (min-width: 1000px)": {
    maxWidth: "40%",
  },
  display: "flex",
  flexDirection: "column",
  margin: fromCurrentUser ? theme.spacing(2, 0, 2, "auto") : theme.spacing(2, "auto", 2, 0),
  width: "100%",
}));
const SkeletonMessageHeader = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));
const SkeletonAttachment = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 1),
  width: "100%",
}));
const SkeletonAttachmentText = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
}));
const HiddenStatus = styled("span")({ ...visuallyHidden });
// #endregion

/**
 * The shape of the rows, rather than a count.
 *
 * Fixed rather than generated, because a placeholder that changes between renders reads as movement
 * on a pane that is meant to be waiting quietly, and a random one cannot be asserted on. The
 * proportions are a plausible short conversation: a couple of longer received messages, shorter
 * replies, alternating sides.
 */
const messagePlaceholders: { fromCurrentUser: boolean; lines: number }[] = [
  { fromCurrentUser: false, lines: 2 },
  { fromCurrentUser: true, lines: 1 },
  { fromCurrentUser: false, lines: 3 },
  { fromCurrentUser: true, lines: 2 },
  { fromCurrentUser: false, lines: 1 },
];

const attachmentPlaceholderCount = 4;

/** Roughly a line of message text, so a two-line placeholder is twice the height of a one-line one. */
const messageLineHeight = 18;

type ForumChatLoadingSkeletonProps = {
  /** Which list the pane is about to show. The two have different shapes, so they wait differently. */
  variant: "messages" | "attachments";
};

/**
 * What the message pane shows while it has nothing to show.
 *
 * It replaces a pair of spinners that used to run at once — a sticky one at the top of the pane for
 * the message and attachment fetches, and a second captioned "Loading..." in the middle for the
 * subscriber and avatar fetch. They were independent booleans that happened to all be true on a
 * first open, so the pane opened with two different spinners at two different sizes and a caption
 * between them.
 *
 * The replacement is one state, chosen by what the reader is actually waiting for: this, whenever
 * the pane is empty and something is on its way to fill it, and the small top spinner only for a
 * further page over content already on screen. They cannot collide, because this one renders only
 * while there is nothing to put a spinner above.
 */
export default function ForumChatLoadingSkeleton(props: Readonly<ForumChatLoadingSkeletonProps>) {
  const { variant } = props;

  return (
    <SkeletonPane data-testid="forum-chat-loading-skeleton" role="status" aria-busy="true">
      <HiddenStatus>
        {variant === "attachments" ? "Loading attachments" : "Loading conversation"}
      </HiddenStatus>
      {variant === "attachments"
        ? Array.from({ length: attachmentPlaceholderCount }, (_, index) => (
            <SkeletonAttachment key={index} aria-hidden="true">
              <Skeleton variant="rounded" width={32} height={40} />
              <SkeletonAttachmentText>
                <Skeleton variant="text" width={`${70 - index * 8}%`} />
                <Skeleton variant="text" width="35%" />
              </SkeletonAttachmentText>
            </SkeletonAttachment>
          ))
        : messagePlaceholders.map((placeholder, index) => (
            <SkeletonMessage key={index} fromCurrentUser={placeholder.fromCurrentUser} aria-hidden="true">
              {!placeholder.fromCurrentUser && (
                <SkeletonMessageHeader>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="text" width={96} />
                </SkeletonMessageHeader>
              )}
              <Skeleton
                variant="rounded"
                height={placeholder.lines * messageLineHeight}
                width={placeholder.fromCurrentUser ? "88%" : "100%"}
                sx={placeholder.fromCurrentUser ? { marginLeft: "auto" } : undefined}
              />
            </SkeletonMessage>
          ))}
    </SkeletonPane>
  );
}
